/*
Purpose: Present inline, collapsible job results inside results views.
Audience: Users who want a quick, readable summary and per-tab breakdown.
What it does:
- Shows Overview tabs and renders mini-cards for vehicles/people/text
- Emits onSeek for timeline clicks to control the media player
- Emits onOpenVehicleDetails to open the dedicated vehicle panel
Interplay:
- Used by results views to render each completed job
- Uses VehicleMiniCard for compact vehicle display
*/
import React, { useRef, useState, useEffect } from 'react';
import { useNotifications } from '../../../notifications/NotificationsProvider';
import '../../styles/results-ui.css';
import { STATUS_COLORS } from '../../utils/providerMap';
import { FaFileAlt, FaCar, FaUsers, FaRunning, FaEye, FaClock, FaSearchPlus, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import VehicleMiniCard from '../cards/VehicleMiniCard';

const JobInlineResults = ({
  job,
  isExpanded,
  onToggle,
  onOpenFull,
  activeTab,
  onTabChange,
  summary,
  targets,
  vehicles,
  plates,
  people,
  activities,
  textItems,
  timeline,
  onOpenVehicleDetails,
  onSeek,
  currentUser,
  onPrivacyToggle
}) => {
  const { notify } = useNotifications();
  const tabsContainerRef = useRef(null);
  const targetsContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showTargetsLeftArrow, setShowTargetsLeftArrow] = useState(false);
  const [showTargetsRightArrow, setShowTargetsRightArrow] = useState(false);
  
  const tabs = [
    { id: 'summary', label: 'Summary', icon: FaFileAlt, visible: true },
    { id: 'vehicles_plates', label: `Vehicles + Plates${vehicles.length ? ` (${vehicles.length})` : ''}`, icon: FaCar, visible: true },
    { id: 'people', label: `People${people.length ? ` (${people.length})` : ''}`, icon: FaUsers, visible: true },
    { id: 'activities', label: `Activities${activities.length ? ` (${activities.length})` : ''}`, icon: FaRunning, visible: true },
    { id: 'text', label: `Text & OCR${textItems.length ? ` (${textItems.length})` : ''}`, icon: FaEye, visible: true },
    { id: 'timeline', label: `Timeline${timeline.length ? ` (${timeline.length})` : ''}`, icon: FaClock, visible: true },
  ].filter(t => t.visible);

  // Check if scroll arrows are needed
  const checkScrollArrows = () => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Check if scroll arrows are needed for targets
  const checkTargetsScrollArrows = () => {
    const container = targetsContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowTargetsLeftArrow(scrollLeft > 0);
    setShowTargetsRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    checkScrollArrows();
    window.addEventListener('resize', checkScrollArrows);
    return () => window.removeEventListener('resize', checkScrollArrows);
  }, [tabs]);

  useEffect(() => {
    checkTargetsScrollArrows();
    window.addEventListener('resize', checkTargetsScrollArrows);
    return () => window.removeEventListener('resize', checkTargetsScrollArrows);
  }, [targets]);

  const scroll = (direction) => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const scrollAmount = 200;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
    
    setTimeout(checkScrollArrows, 300);
  };

  const scrollTargets = (direction) => {
    const container = targetsContainerRef.current;
    if (!container) return;
    
    const scrollAmount = 200;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
    
    setTimeout(checkTargetsScrollArrows, 300);
  };

  const plateMap = plates.reduce((m, p) => {
    const key = p.parentVehicleId || '__standalone__';
    if (!m[key]) m[key] = [];
    m[key].push(p);
    return m;
  }, {});

  const statusColor = STATUS_COLORS[job?.status] || '#22c55e';
  const hexToRgba = (hex, alpha) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return `rgba(34, 197, 94, ${alpha || 0.06})`;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${typeof alpha === 'number' ? alpha : 0.06})`;
  };
  const containerStyle = {
    background: hexToRgba(statusColor, 0.06),
    border: `1px solid ${statusColor}33`,
    borderRadius: 10,
    marginTop: 6
  };

  // Check if current user is the job creator
  const isOwner = currentUser && job && (currentUser.uid === job.userId || currentUser.uid === job.createdBy);
  const isPublic = job?.isPublic || false;
  const hasAccess = isOwner || isPublic; // Will add hasPurchasedAccess check later
  
  // Check if this job has a price or requires request
  const hasPrice = job?.accessPrice && job.accessPrice > 0;
  
  // Determine button text based on access and pricing
  const getAccessButtonText = () => {
    if (hasAccess) return 'Deep Dive';
    if (hasPrice) return `Unlock Access • $${job.accessPrice.toFixed(2)}`;
    return 'Request Access';
  };

  return (
    <div className="inline-report" style={containerStyle}>
      <div className="inline-report-header">
        <div className="report-title">
          Results
          {/* Show privacy status for non-owners */}
          {!isOwner && !hasAccess && (
            <span className="privacy-badge locked">
              <FaSearchPlus style={{ fontSize: '0.75rem' }} /> Premium
            </span>
          )}
        </div>
        
        {/* Privacy toggle for owners */}
        {isOwner && onPrivacyToggle && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginRight: 'auto',
            marginLeft: '15px'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => {
                  e.stopPropagation();
                  onPrivacyToggle(job.id, e.target.checked);
                }}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ color: isPublic ? '#22c55e' : '#888' }}>
                {isPublic ? 'Public' : 'Private'}
              </span>
            </label>
          </div>
        )}
        
        <div className="report-actions">
          <button 
            className="btn-secondary btn-sm" 
            onClick={(e)=>{
              e.stopPropagation(); 
              // Only allow Show Details if owner or if results are public
              if (hasAccess) {
                onToggle();
              } else {
                notify({ severity: 'warning', title: 'Premium Content', message: 'Purchase or request access to view details.' });
              }
            }}
            title={!hasAccess ? 'Premium content - purchase or request access' : ''}
          >
            {isExpanded ? 'Hide' : 'Show'}
          </button>
          <button 
            className={`btn-sm ${hasAccess ? 'btn-primary' : 'btn-access-locked'}`}
            onClick={(e)=>{
              e.stopPropagation(); 
              onOpenFull(); // Always call - parent will handle routing to purchase modal if needed
            }}
            title={hasAccess ? 'Deep dive into detailed analysis with synchronized video' : (hasPrice ? 'Purchase access to view full results' : 'Request access to view full results')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {hasAccess ? (
              <FaSearchPlus style={{ fontSize: '11px' }} />
            ) : (
              <>
                <FaSearchPlus style={{ fontSize: '11px' }} />
                {getAccessButtonText()}
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="inline-report-body">
          <div className="inline-tabs-wrapper">
            {showLeftArrow && (
              <button 
                className="tab-scroll-arrow tab-scroll-left"
                onClick={() => scroll('left')}
                aria-label="Scroll tabs left"
              >
                <FaChevronLeft />
              </button>
            )}
            <div 
              className="inline-tabs" 
              role="tablist"
              ref={tabsContainerRef}
              onScroll={checkScrollArrows}
            >
              {tabs.map(t => {
                const Icon = t.icon;
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    className={`inline-tab ${isActive ? 'active' : ''}`}
                    onMouseDown={(e)=>{e.stopPropagation(); onTabChange(t.id);}}
                    onClick={(e)=>e.stopPropagation()}
                    role="tab"
                    aria-selected={isActive}
                  >
                    <Icon className="inline-tab-icon" /> {t.label}
                  </button>
                );
              })}
            </div>
            {showRightArrow && (
              <button 
                className="tab-scroll-arrow tab-scroll-right"
                onClick={() => scroll('right')}
                aria-label="Scroll tabs right"
              >
                <FaChevronRight />
              </button>
            )}
          </div>

          {activeTab === 'summary' && (
            <div className="report-section">
              <div className="section-label">Summary</div>
              <div className="section-mono">{summary}</div>
              {targets?.length > 0 && (
                <div className="report-section" style={{ marginTop: 8 }}>
                  <div className="section-label">Targets</div>
                  <div className="targets-scroll-wrapper">
                    {showTargetsLeftArrow && (
                      <button 
                        className="targets-scroll-arrow targets-scroll-left"
                        onClick={() => scrollTargets('left')}
                        aria-label="Scroll targets left"
                      >
                        <FaChevronLeft />
                      </button>
                    )}
                    <div 
                      className="targets-row"
                      ref={targetsContainerRef}
                      onScroll={checkTargetsScrollArrows}
                    >
                      {targets.map((t, idx) => (
                        <span key={idx} className="target-chip">{String(t).replaceAll('_',' ')}</span>
                      ))}
                    </div>
                    {showTargetsRightArrow && (
                      <button 
                        className="targets-scroll-arrow targets-scroll-right"
                        onClick={() => scrollTargets('right')}
                        aria-label="Scroll targets right"
                      >
                        <FaChevronRight />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'vehicles_plates' && (
            <div className="report-section">
              {(vehicles.length === 0 && plates.length === 0) ? (
                <div className="section-mono">No vehicles or plates detected.</div>
              ) : (
                <div className="mini-cards">
                  {vehicles.map(v => (
                    <VehicleMiniCard key={v.id} vehicle={v} platesForVehicle={plateMap[v.id] || []} onOpenDetails={onOpenVehicleDetails} />
                  ))}
                  {(plateMap['__standalone__'] || []).map(p => (
                    <VehicleMiniCard key={p.id} vehicle={{ id: p.id, make: 'Unknown', model: 'Unknown', color: 'Unknown', confidence: p.confidence || 0.7, licensePlates: [{ plateNumber: p.plateNumber }] }} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'people' && (
            <div className="report-section">
              {people.length === 0 ? (
                <div className="section-mono">No people detected.</div>
              ) : (
                <div className="mini-cards">
                  {people.map(pr => (
                    <div key={pr.id} className="mini-card">
                      <div className="mini-title"><span>Person</span><span className="mini-conf">{Math.round((pr.confidence||0)*100)}%</span></div>
                      <div className="mini-body">
                        <div className="mini-row"><span className="k">Age</span><span className="v">{pr.estimatedAge}</span></div>
                        <div className="mini-row"><span className="k">Gender</span><span className="v">{pr.gender}</span></div>
                        <div className="mini-row"><span className="k">Clothing</span><span className="v">{pr.clothing}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="report-section">
              {activities.length === 0 ? (
                <div className="section-mono">No activities detected.</div>
              ) : (
                <div className="mini-cards">
                  {activities.map(a => (
                    <div key={a.id} className="mini-card">
                      <div className="mini-title"><span>Activity</span><span className="mini-conf">{Math.round((a.confidence||0)*100)}%</span></div>
                      <div className="mini-body">
                        <div className="mini-row"><span className="k">Event</span><span className="v">{a.activity}</span></div>
                        <div className="mini-row"><span className="k">Time</span><span className="v">{a.timestamp}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'text' && (
            <div className="report-section">
              {textItems.length === 0 ? (
                <div className="section-mono">No text detected.</div>
              ) : (
                <div className="mini-cards">
                  {textItems.map(t => (
                    <div key={t.id} className="mini-card">
                      <div className="mini-title"><span>Text</span><span className="mini-conf">{Math.round((t.confidence||0)*100)}%</span></div>
                      <div className="mini-body">
                        <div className="mini-row"><span className="k">Value</span><span className="v">{t.text}</span></div>
                        <div className="mini-row"><span className="k">Time</span><span className="v">{t.timestamp}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="report-section">
              {timeline.length === 0 ? (
                <div className="section-mono">No timeline events.</div>
              ) : (
                <div className="inline-timeline">
                  {timeline.map(e => (
                    <div key={e.id} className="inline-timeline-item" onClick={(ev)=>{ ev.stopPropagation(); onSeek && onSeek(e.t_s || 0); }}>
                      <div className="inline-timeline-time">{(e.t_s || 0).toFixed(2)}s</div>
                      <div className="inline-timeline-type">{e.type}</div>
                      <div className="inline-timeline-details">{e.details}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(JobInlineResults);


